import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { LatLngExpression } from 'leaflet';

interface MapContextType {
  userLocation: LatLngExpression | null;
  gazaPosition: LatLngExpression | null;
  selectedDataLayer: string | null;
  isOverlayDragging: boolean;
  isInfoPanelExpanded: boolean;
  setUserLocation: (location: LatLngExpression) => void;
  setGazaPosition: (position: LatLngExpression) => void;
  setSelectedDataLayer: (layer: string | null) => void;
  setIsOverlayDragging: (dragging: boolean) => void;
  setIsInfoPanelExpanded: (expanded: boolean) => void;
  resetGazaPosition: () => void;
}

const MapContext = createContext<MapContextType | undefined>(undefined);

export const useMap = () => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMap must be used within MapProvider');
  }
  return context;
};

interface MapProviderProps {
  children: ReactNode;
}

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
  const [userLocation, setUserLocation] = useState<LatLngExpression | null>(null);
  const [gazaPosition, setGazaPosition] = useState<LatLngExpression | null>(null);
  const [selectedDataLayer, setSelectedDataLayer] = useState<string | null>(null);
  const [isOverlayDragging, setIsOverlayDragging] = useState(false);
  const [isInfoPanelExpanded, setIsInfoPanelExpanded] = useState(false);

  const resetGazaPosition = useCallback(() => {
    if (userLocation) {
      setGazaPosition(userLocation);
    }
  }, [userLocation]);

  return (
    <MapContext.Provider
      value={{
        userLocation,
        gazaPosition,
        selectedDataLayer,
        isOverlayDragging,
        isInfoPanelExpanded,
        setUserLocation,
        setGazaPosition,
        setSelectedDataLayer,
        setIsOverlayDragging,
        setIsInfoPanelExpanded,
        resetGazaPosition,
      }}
    >
      {children}
    </MapContext.Provider>
  );
};